import { motion } from 'framer-motion';
import { Download, Users, Activity, Target } from 'lucide-react';
import { useInView } from '../../hooks/useAnimations';

const iconMap = { Download, Users, Activity, Target };

const steps = [
  { step: 1, title: 'Capture Leads', description: 'Import leads from multiple sources or capture them directly through web forms and API integrations.', icon: 'Download' },
  { step: 2, title: 'Allocate Team', description: 'Automatically assign leads to the right TeleValleyers based on skills, workload, and performance history.', icon: 'Users' },
  { step: 3, title: 'Track Calls', description: 'Monitor all call activities, recordings, and outcomes in real-time with detailed analytics.', icon: 'Activity' },
  { step: 4, title: 'Convert Customers', description: 'Close deals faster with intelligent insights, automated follow-ups, and smart scheduling.', icon: 'Target' },
];

const HowItWorks = () => {
  const [ref, isInView] = useInView({ threshold: 0.1 });

  return (
    <section id="how-it-works" ref={ref} className="py-24 relative overflow-hidden bg-gradient-to-b from-slate-50 to-white">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
          className="absolute top-1/4 right-0 w-[400px] h-[400px] rounded-full orb-purple"
        />
        <motion.div
          animate={{ x: [0, -40, 0], y: [0, 30, 0] }}
          transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
          className="absolute bottom-1/4 left-0 w-[500px] h-[500px] rounded-full orb-indigo"
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-16"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 text-sm text-purple-700 mb-6">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-purple-500"></span>
            </span>
            Simple Process
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>
            How <span className="text-gradient">It Works</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Get started in minutes and transform your sales operations with our streamlined workflow.
          </p>
        </motion.div>

        {/* Steps */}
        <div className="relative">
          {/* Connection Line */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-300 to-transparent -translate-y-1/2" />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => {
              const IconComponent = iconMap[step.icon];
              return (
                <motion.div
                  key={step.step}
                  initial={{ opacity: 0, y: 30 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: index * 0.15 }}
                  className="relative"
                >
                  <div className="bg-white rounded-2xl p-8 border border-purple-100 shadow-lg shadow-purple-500/5 hover:shadow-xl hover:shadow-purple-500/10 transition-all duration-300 group">
                    {/* Step Number */}
                    <div className="absolute -top-4 left-8">
                      <motion.div
                        whileHover={{ scale: 1.1 }}
                        className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-sm font-bold text-white"
                      >
                        {step.step}
                      </motion.div>
                    </div>

                    {/* Icon */}
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center mb-6 group-hover:from-purple-200 group-hover:to-indigo-200 transition-colors"
                    >
                      <IconComponent className="w-8 h-8 text-purple-600" />
                    </motion.div>

                    <h3 className="text-xl font-semibold text-slate-900 mb-3" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>{step.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{step.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;